import * as React from "react";
import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";
import {Image} from "@arctheme-components/elements/Image/Image";
import {Link} from "@arctheme-components/elements/Link/Link";

export const MegamenuFeaturedContent = ({ settings, megamenuSM }) => {
  const isMobile = useMobileBreakpoint(settings.mobile_breakpoint);
  
  const blocks = settings.blocks.filter(block => block.type === 'article').slice(0, 2);
  // Use the articles from megamenuSM which gets updated when links are hovered
  const articlesToShow = blocks || [];
  const hasArticles = articlesToShow && articlesToShow.length > 0;

  if (!hasArticles) {
    return null; // Return nothing if there are no articles
  }
  
  // Apply custom styling from settings
  const featuredContentStyles = {
    backgroundColor: settings.section.featured_content_bg_color || 'transparent',
    border: settings.section.featured_content_border ? 
      `${settings.section.featured_content_border_width || 1}px ${settings.section.featured_content_border_style || 'solid'} ${settings.section.featured_content_border_color || '#e2e8f0'}` : 
      'none',
    borderRadius: settings.section.featured_content_border_radius ? `${settings.section.featured_content_border_radius}px` : '0',
    padding: settings.section.featured_content_padding ? `${settings.section.featured_content_padding}px` : '0',
  };
  
  // Styles for article items
  const articleItemStyles = {
    marginBottom: settings.section.article_spacing ? `${settings.section.article_spacing}px` : '0',
    paddingBottom: settings.section.article_spacing ? `${settings.section.article_spacing}px` : '0',
    borderBottom: settings.section.article_dividers ? `1px solid ${settings.section.article_divider_color || '#e2e8f0'}` : 'none',
  };

  return (
    <div className="megamenu-featured-content w-full" style={featuredContentStyles}>
      {settings.section.featured_content_title && (
        <h3 className={`${settings.section.featured_title_style || 'text-lg font-medium'}`}>
          {settings.section.featured_content_title}
        </h3>
      )}
      
      <div className="megamenu-articles-container">
        {articlesToShow.map((article, index) => (
          <article 
            key={`featured-article-${index}`} 
            className="megamenu-featured-article" 
            style={articleItemStyles}
          >
            <div className="flex items-start min-w-[320px]">
              {article.settings.image && (
                <div className="shrink-0 mr-3" style={{ 
                  width: settings.section.article_image_width ? `${settings.section.article_image_width}px` : '80px',
                  height: settings.section.article_image_height ? `${settings.section.article_image_height}px` : 'auto',
                }}>
                  <a href={article.settings.url}>
                    <Image
                      imageUrl={article.settings.image}
                      classes={`w-full h-full object-cover ${settings.section.article_image_radius ? `rounded-[${settings.section.article_image_radius}px]` : 'rounded'}`}
                      alt={article.settings.title}
                    />
                  </a>
                </div>
              )}
              
              <div className="flex-grow flex flex-col gap-2">
                <h4 className={`megamenu-featured-article-title ${settings.section.article_title_style || 'font-medium'}`}>
                  <a
                    href={article.settings.url}
                    className="no-underline"
                  >
                    {article.settings.title}
                  </a>
                </h4>

                <p className="b3--bold text-rhv-orange">
                  {article.settings.read_time} min read
                </p>
                
                {article.settings.description && (
                  <p className={`megamenu-featured-article-description ${settings.section.article_description_style || 'text-sm mt-1'}`}>
                    <a
                      href={article.settings.url}
                      className="no-underline"
                    >
                      {article.settings.description}
                    </a>
                  </p>
                )}
                
                {article.settings.url && (
                  <Link
                    link={article.settings.url}
                    classes={`megamenu-featured-article-link py-2.5 inline-block ${settings.section.article_link_style || 'text-sm font-medium mt-2 inline-block hover:underline'}`}
                  >
                    {article.settings.linkText || "Read More"}
                    {settings.section.article_link_icon && (
                      <i className={`icon ${settings.section.article_link_icon} ml-1`}></i>
                    )}
                  </Link>
                )}
              </div>
            </div>
          </article>
        ))}
        
        {settings.section.view_all_url && (
          <div className="mt-4">
            <Link link={settings.section.view_all_url}>
              <div className="flex items-center cursor-pointer py-2.5">
                <span className={settings.section.view_all_link_style}>
                  {settings.section.view_all_text || "View All"}
                </span>

                <i className={`icon ${settings.section.view_all_icon || 'icon-arrow-back rotate-180 inline-block'} ml-1 text-lg`}></i>
              </div>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};